/* eslint-disable react-hooks/exhaustive-deps */
import LoadingContent from 'components/LoadingContent';
import { useFormik } from 'formik';
import React, { useEffect } from 'react';
import TopProcessBar from 'screen/selectCollege/TopProcessBar';
import { Container, Form, Spinner, Button } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Cookies from 'js-cookie';
import { useHistory, useLocation } from 'react-router-dom';
import { routers } from 'router/router';
import { logout } from 'app/basicInformationSlice/basicInformationSlice';
import { authenticateSettings } from 'components/helper/appConfig';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import { checkEnableParseJSON, reorder, setArrayLength } from 'components/helper/helper';
import HonorsConfig from './components/HonorsConfig';
import {
  getActivitiesAndHonors,
  updateActivitiesAndHonors,
} from 'app/activitiesAndHonors/activitiesAndHonorsSlice';
import { debounce } from 'lodash';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import { ExpandMore } from './Activities';
import Grid from '@mui/joy/Grid';
import { ModalConfirm } from 'screen/common/ModalConfirm';
import websocketService from 'services/websocketService';
import { PAGE_NAME } from 'constants/common';
import pageStatusService from 'services/pageStatusService';
import { getAccessToken, getUserId, removeUserId, removeAccessToken } from 'utils/sessionStorage';

interface Props {}

const Honors = (props: Props) => {
  const [honorsData, setHonorsData] = React.useState<any[]>([{}]);
  const [loading, setLoading] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth <= 768);
  const activitiesAndHonors = useAppSelector((store) => store.activitiesAndHonors);
  const [reviewedAndComplete, setReviewedAndComplete] = React.useState(false);
  const onReviewdAndComplete = () => { 
    const newValue = !reviewedAndComplete;
    setReviewedAndComplete(newValue);
    websocketService.finishReviewAndComplete(PAGE_NAME.HONORS, newValue);
  }
  const [expandAll, setExpandAll] = React.useState(false);
  const [expandActive, setExpandActive] = React.useState<number>(0);

  const query = new URLSearchParams(useLocation().search);
  const check = query.get('check');
  const indexEpand = query.get('index');
  
  let studentId = getUserId();
  let token = getAccessToken();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const formik = useFormik({
    initialValues: {
      number_of_honor: '0',
      honor_array: '',
    },
    onSubmit: async (values) => {
      studentId = getUserId();
      token = getAccessToken();
      if (token && studentId) {
        const newValues = {
          ...values,
          student_id: studentId,
          honor_array: JSON.stringify(honorsData),
        };
        await dispatch(updateActivitiesAndHonors(newValues));
      } else {
        handleLogout();
      }
    },
  });

  const handleLogout = () => {
    dispatch(logout());
    removeAccessToken();
    removeUserId();
    history.push(routers.login.path);
  };

  const handleExpandAll = () => {
    setExpandAll(!expandAll);
    if (!expandAll) {
      setExpandActive(honorsData.length);
    } else {
      setExpandActive(0);
    }
  };

  const handleExpandActive = (value: any) => {
    value
      ? setExpandActive(Math.abs(expandActive - 1))
      : setExpandActive(Math.abs(expandActive + 1));
  };

  useEffect(() => {
    if (expandActive === honorsData.length) {
      setExpandAll(true);
    }

    if (expandActive === 0) {
      setExpandAll(false);
    }
  }, [expandActive, honorsData.length]);

  const handleContinue = () => {
    formik.handleSubmit();
    history.push(routers.educationalPrograms.path);
  };

  const getDetail = async () => {
    studentId = getUserId();
    token = getAccessToken();
    if (token && studentId) {
      setLoading(true);
      const pageStatus: {
        student_completed: boolean;
      } = await pageStatusService.getPageStatusByPageName(studentId, PAGE_NAME.HONORS);
      if(pageStatus) {
        setReviewedAndComplete(pageStatus.student_completed);
      }
      const res = await dispatch(getActivitiesAndHonors(studentId));
      const dataLoaded = res.payload;
      if (dataLoaded) {
        formik.setValues({ ...formik.values, ...dataLoaded });
        if (dataLoaded.honor_array) {
          setHonorsData(checkEnableParseJSON(dataLoaded.honor_array));
        }
      }
      setLoading(false);
      formik.setStatus('idle');
    } else {
      handleLogout();
    }
  };

  React.useEffect(() => {
    getDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    setHonorsData(setArrayLength(honorsData, formik.values.number_of_honor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.number_of_honor]);

  const [forceRenderKey, setForceRenderKey] = React.useState(0);

  const onDragEnd = async (result: any) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }
    const dataList = reorder(honorsData, result.source.index, result.destination.index);
    setHonorsData([...dataList]);
    setForceRenderKey(forceRenderKey + 1);
  };

  // Auto save function ==========
  React.useEffect(() => {
    debounceSaving();
  }, [formik.values, honorsData]);
  const debounceSaving = React.useCallback(
    debounce(() => {
      if (!loading) formik.handleSubmit();
    }, 1500),
    []
  );
  // Auto save function ==========

  const handleAddHonors = () => {
    if (honorsData.length > 9) return;
    setHonorsData([...honorsData, {}]);
  };

  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [removeIndex, setRemoveIndex] = React.useState<number | null>(null);

  const handleClose = () => {
    setOpenConfirm(false);
    setRemoveIndex(null);
  };
  const handleConfirm = (index: number) => {
    const newHonorsData = [...honorsData];
    newHonorsData.splice(index, 1);
    setHonorsData(newHonorsData);
    setOpenConfirm(false);
    setRemoveIndex(null);
  };
  const handleRemoveHonor = (index: number) => {
    setRemoveIndex(index);
    setOpenConfirm(true);
  };

  return loading ? (
    <LoadingContent />
  ) : (
    <>
      <TopProcessBar
        stepProcess={[
          { percentOnProcess: 30, title: 'Honors & Awards', specialId: 'Honors_Awards' },
          { percentOnProcess: 60, title: 'Honor & Award Details', specialId: 'Honors_Awards_Details' },
          { percentOnProcess: 90, title: 'Selected Apps', specialId: 'Selected_Apps' },
        ]}
        title="Honors & Awards"
        process={1}
        icon={<StackedLineChartIcon />}
        isMobile={isMobile}
      />

      <Container 
        className={`main-container gm-basic-info-page ${isMobile ? 'p-mobile-2' : ''}`}
        style={{
          paddingLeft: isMobile ? '12px' : undefined,
          paddingRight: isMobile ? '12px' : undefined,
          paddingTop: isMobile ? '120px' : undefined,
          maxWidth: '100vw',
          overflow: 'hidden'
        }}
      >
        <div className={`gm-section-card ${isMobile ? 'p-mobile-2' : 'px-4 py-4'}`}>
          <Form
            style={{ 
              maxWidth: isMobile ? '100%' : '900px',
              width: '100%'
            }}
            onSubmit={formik.handleSubmit}
            className="gm-form-grid"
          >
            <div id="Honors_Awards" className={`gm-section-title ${isMobile ? 'mb-2' : 'mb-4'}`}>
              <span style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 600 }}>
                Honors
              </span>
            </div>
            <Grid container spacing={isMobile ? 1 : 2} className="gm-form-fields">
              <Grid xs={12}>
                <div className="p-3" style={{ fontSize: '1rem' }}>
                  If you have received any honors or awards related beginning with the ninth grade or
                  international equivalent, please indicate them here.
                </div>
              </Grid>
              <Grid xs={12}>
                <div className="bg-light p-3 rounded" style={{ fontSize: '1rem' }}>
                  <div>App Notes:</div>
                  <div>
                    Common App - Allows you to report any honors related to your academic achievements
                    beginning with the ninth grade or international equivalent. If you received more
                    than five, please add the five most important to you.*
                  </div>
                  <div>UC App - Allow you to report up to 20 activities and honors combined.</div>
                </div>
              </Grid>
              {expandActive===0 && (
                <div id="Honors_Awards_Details"></div>
              )}
              <Grid xs={12}>
                <DragDropContext onDragEnd={onDragEnd} key={forceRenderKey}>
                  {honorsData.length > 0 && (
                    <div className="d-flex justify-content-end mt-3">
                      <ExpandMore
                        aria-expanded={expandAll}
                        aria-label="show all"
                        onClick={handleExpandAll}
                      >
                        {!expandAll ? (
                          <KeyboardDoubleArrowDownIcon />
                        ) : (
                          <KeyboardDoubleArrowUpIcon />
                        )}
                      </ExpandMore>
                    </div>
                  )}
                  <Droppable droppableId="list">
                    {(provided) => (
                      <div className='w-100 bg-white py-2 box-collapse rounded-3' ref={provided.innerRef} {...provided.droppableProps}>
                        {honorsData.map((item, index) => (
                          <div key={index + '-' + honorsData.length}>
                            <Draggable draggableId={index + '-' + honorsData.length} index={index} isDragDisabled={isMobile}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...(!isMobile ? provided.dragHandleProps : {})}
                                >
                                  <div className="position-relative">
                                    <HonorsConfig
                                      handleSave={(values) => {
                                        const newActivitiesData = [...honorsData];
                                        newActivitiesData.splice(index, 1, values);
                                        setHonorsData(newActivitiesData);
                                      }}
                                      item={item}
                                      indexNumber={`${index + 1}/${honorsData.length}`}
                                      expandAll={expandAll}
                                      handleExpandActive={handleExpandActive}
                                      check={check}
                                      indexExpand={`${indexEpand}/${honorsData.length}`}
                                      isMobile={isMobile}
                                      disabled={reviewedAndComplete}
                                    />
                                    <RemoveCircleOutlineIcon
                                      className="position-absolute text-field-light pointer"
                                      style={{ 
                                        right: isMobile ? '-12px' : '-24px', 
                                        top: isMobile ? '8px' : '12px',
                                        fontSize: isMobile ? '20px' : '24px'
                                      }}
                                      onClick={() => {
                                        if (reviewedAndComplete) return;
                                        handleRemoveHonor(index);
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          </div>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </Grid>
              <Grid xs={12}>
                <div className="my-3 w-100 d-flex flex-column align-items-center">
                  <Button variant="primary" className="p-2 px-5" onClick={handleAddHonors} disabled={reviewedAndComplete}>
                    Add Honor or Award
                  </Button>
                </div>
              </Grid>
            </Grid>

            <div className={`${isMobile ? 'my-2' : 'my-3'} d-flex ${isMobile ? 'flex-column' : 'justify-content-between'} border-top ${isMobile ? 'pt-2' : 'pt-3'}`}>
              <div className={`${isMobile ? 'w-100 mb-3' : ''} d-flex justify-content-start`}>
                <div className="d-flex flex-column align-items-start">
                  <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                    <input
                      type="checkbox"
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #6c757d',
                        borderRadius: '2px',
                      }}
                      checked={reviewedAndComplete}
                      onClick={onReviewdAndComplete}
                    />
                    <span
                      style={{
                        fontSize: isMobile ? '14px' : '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Reviewed & Complete!
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={`${isMobile ? 'w-100' : 'col col-md-6'} text-center ${isMobile ? '' : 'text-md-end'}`}>
                <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'flex-row'} justify-content-center`}>
                  <Button 
                    variant="primary" 
                    type="submit" 
                    className={`${isMobile ? 'w-100 py-3' : 'p-2 px-5 mx-2'} gm-btn-save`}
                    style={{
                      fontSize: isMobile ? '16px' : '14px',
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    {activitiesAndHonors.status === 'loading' ? (
                      <Spinner size="sm" animation="border" variant="light" />
                    ) : (
                      'Save'
                    )}
                  </Button>
                  <Button 
                    variant="primary" 
                    className={`${isMobile ? 'w-100 py-3' : 'p-2 px-5'} gm-btn-continue`}
                    onClick={() => handleContinue()}
                    style={{
                      fontSize: isMobile ? '16px' : '14px',
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </Container>
      <ModalConfirm 
        open={openConfirm}
        handleClose={handleClose} 
        handleConfirm={() => {
          if (removeIndex !== null) handleConfirm(removeIndex);
        }}
        title=""
        message="Are you sure you want to remove this honor/award?"
        confirmText="Remove"
        cancelText="Cancel"
      />
    </>
  );
};

export default Honors;
